import React, { useMemo } from "react";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";

/**
 * Reusable SEO block for every route. Emits:
 *   - <title> + meta description
 *   - Open Graph + Twitter card
 *   - hreflang alternates for all 5 supported languages
 *   - canonical URL
 *   - optional JSON-LD (schema.org) payload
 *
 * Path defaults to window.location.pathname so callers only need to pass
 * page-specific copy. Uses SITE_ORIGIN so alternates work on both preview
 * and production (greenbeltx.com).
 */
const SUPPORTED_LANGS = ["en", "pt", "es", "fr", "ar"];

// Canonical production origin. hreflang alternates always point at this so
// Google sees the same canonical set regardless of which environment
// crawled a link (preview vs prod).
const SITE_ORIGIN = "https://greenbeltx.com";

const DEFAULT_OG_IMAGE = "https://customer-assets.emergentagent.com/job_credit-marketplace-11/artifacts/xeso8axu_image.png";

export default function SEO({
  title,
  description,
  path,
  image = DEFAULT_OG_IMAGE,
  type = "website",
  jsonLd,
  noindex = false,
}) {
  const { i18n } = useTranslation();
  const lng = i18n.language || "en";

  const pathname = useMemo(() => {
    if (path) return path;
    if (typeof window !== "undefined") return window.location.pathname;
    return "/";
  }, [path]);

  const canonical = `${SITE_ORIGIN}${pathname}`;
  const fullTitle = title ? `${title} · GreenBelt` : "GreenBelt · Verified UCS Projects Exchange";

  return (
    <Helmet>
      <html lang={lng} dir={lng === "ar" ? "rtl" : "ltr"} />
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {noindex && <meta name="robots" content="noindex,nofollow" />}

      {/* Canonical + hreflang alternates */}
      <link rel="canonical" href={canonical} />
      {SUPPORTED_LANGS.map((lang) => (
        <link
          key={lang}
          rel="alternate"
          hrefLang={lang}
          href={`${canonical}${canonical.includes("?") ? "&" : "?"}lang=${lang}`}
        />
      ))}
      <link rel="alternate" hrefLang="x-default" href={canonical} />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="GreenBelt" />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={image} />
      <meta property="og:locale" content={lng === "en" ? "en_US" : lng === "pt" ? "pt_BR" : lng === "es" ? "es_ES" : lng === "fr" ? "fr_FR" : "ar_SA"} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* Structured data */}
      {jsonLd && (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      )}
    </Helmet>
  );
}

/** Site-wide Organization schema — mount once on the landing page */
export const ORGANIZATION_JSON_LD = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "GreenBelt",
  legalName: "GreenBelt Exchange",
  url: SITE_ORIGIN,
  logo: DEFAULT_OG_IMAGE,
  description:
    "GreenBelt is the exchange for the UCS (Unidade de Crédito de Sustentabilidade) — a regulated Brazilian ecological asset backed by B3 ISINs, Law 13.986/2020 (CPR Verde), and the BMV Global methodology.",
  email: "investors@greenbelt.io",
  identifier: [
    { "@type": "PropertyValue", propertyID: "KSA Business License", value: "7054921197" },
  ],
  areaServed: ["SA", "BR", "US", "GB", "EU"],
  sameAs: [
    "https://bmv.global",
    "https://www.b3.com.br",
  ],
};

export { SITE_ORIGIN };
